import { ref, onUnmounted } from 'vue'

interface Message {
  [key: string]: any
}

export function useWebSocket(
  url: string,
  onMessage?: (msg: Message) => void,
  retryInterval = 3000,
  maxRetries = Infinity
) {
  const maxConsoleMessages = 200
  const ws = ref<WebSocket | null>(null)
  const messages = ref<Message[]>([])
  const isConnected = ref(false)
  const lastMessageAt = ref(0)
  const hasError = ref(false)
  let reconnectTimeout: ReturnType<typeof setTimeout> | null = null
  let retries = 0
  let intentionallyClosed = false

  const connect = () => {
    intentionallyClosed = false
    if (ws.value) {
      ws.value.onopen = null
      ws.value.onmessage = null
      ws.value.onclose = null
      ws.value.onerror = null
      ws.value.close()
      ws.value = null
    }

    const token = getSessionToken()
    if (!token) return
    ws.value = new WebSocket(url, ['bearer', token])

    ws.value.onopen = () => {
      console.log('WebSocket connected', url)
      isConnected.value = true
      hasError.value = false
      retries = 0
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }
    }

    ws.value.onmessage = (event: MessageEvent) => {
      lastMessageAt.value = Date.now()
      let data: Message
      try {
        data = JSON.parse(event.data)
      } catch {
        data = { raw: event.data }
      }
      console.debug('WebSocket message', url, data)
      if ('console' in data || 'raw' in data) {
        messages.value = [data, ...messages.value.slice(0, maxConsoleMessages - 1)]
      }
      if (onMessage) onMessage(data)
    }

    ws.value.onclose = (event) => {
      lastMessageAt.value = 0
      if (event.code === 1008) {
        clearSessionToken()
        intentionallyClosed = true
        window.location.replace('/login')
      }
      console.log('WebSocket closed')
      isConnected.value = false
      if (!intentionallyClosed) scheduleReconnect()
    }

    ws.value.onerror = (err) => {
      console.error('WebSocket error', err)
      hasError.value = true
      isConnected.value = false
      if (!intentionallyClosed) scheduleReconnect()
    }
  }

  const scheduleReconnect = () => {
    if (retries >= maxRetries) return
    if (!reconnectTimeout) {
      const exponentialDelay = Math.min(retryInterval * (2 ** retries), 30000)
      const delayWithJitter = exponentialDelay + Math.random() * 500
      reconnectTimeout = setTimeout(() => {
        console.log('Attempting to reconnect WebSocket...')
        retries++
        reconnectTimeout = null
        connect()
      }, delayWithJitter)
    }
  }

  const sendMessage = (msg: Message) => {
    if (ws.value && ws.value.readyState === WebSocket.OPEN) {
      ws.value.send(JSON.stringify(msg))
    }
  }

  const clearMessages = () => {
    messages.value = []
  }

  const close = () => {
    lastMessageAt.value = 0
    intentionallyClosed = true
    if (ws.value) {
      ws.value.onclose = null
      ws.value.onerror = null
      ws.value.close()
      ws.value = null
    }
    isConnected.value = false
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = null
    }
  }

  onUnmounted(() => close())

  return { ws, messages, connect, sendMessage, close, clearMessages, isConnected, lastMessageAt, hasError }
}
